import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useUserPortfolioLink } from '#app/hooks/usePortfolioLink.js';
import {
  ChevronRightIcon,
  RecommendationStarFullIcon,
} from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { css, styled } from 'styled-components';

const StyledText = styled(Text)`
  position: relative;
`;

const Divider = styled.hr`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray30};
    height: 1px;
    border: 0;
  `}
`;

const RecommendedStarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const RecommendedActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: 16px;
    border-radius: 10px;
    border: 1px;
    gap: 16px;
    border: 1px solid ${theme.colorsV2.gray30};
    margin-top: 24px;
  `}
`;

const StyledLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 36px;
  gap: 16px;
`;

const BlackRecommendationStarFullIcon = styled(RecommendationStarFullIcon)`
  ${({ theme }) => css`
    path {
      fill: ${theme.colorsV2.black};
    }
  `}
`;

const MaybeHideAvatars = styled.div<{
  hide: boolean;
  isSidenavDocked: boolean;
}>`
  ${({ hide, isSidenavDocked, theme }) => css`
    ${hide
      ? css`
          display: none;
        `
      : undefined}

    @media (min-width: ${theme.breakpoints
      .lg}) and (max-width: ${isSidenavDocked ? css`1650px` : css`1400px`}) {
      display: none;
    }
  `}
`;

type PortfolioAndRecommendationsProps = {
  readonly displayUsername: string;
  readonly hasActiveSubscriptionAndPublishedPortfolio: boolean;
  readonly userReviews: {
    count: number;
    reviews: Array<{
      author: {
        avatarImage: null | {
          uid: string;
        };
        id: string;
      };
      id: string;
    }>;
  };
};

export const PortfolioAndRecommendations = ({
  displayUsername,
  hasActiveSubscriptionAndPublishedPortfolio,
  userReviews,
}: PortfolioAndRecommendationsProps) => {
  const { getPortfolioUrl } = useUserPortfolioLink();
  const { track } = useAnalytics();

  const portfolioUrl = getPortfolioUrl({
    urlParameters: {
      utm_campaign: 'profile_sidebar',
    },
    username: displayUsername,
  });

  const recommendationsUrl = getRoute('/@username/recommendations', {
    args: {
      username: displayUsername,
    },
  });

  const isSidenavDocked = useHasUserInterfaceFlags('DOCKED_SIDE_NAVIGATION');

  if (!hasActiveSubscriptionAndPublishedPortfolio && userReviews.count === 0) {
    return null;
  }

  const recommendationAvatars = userReviews.reviews.map((review) => ({
    avatarImage: review.author.avatarImage
      ? { uid: review.author.avatarImage.uid }
      : null,
    key: review.author.id,
  }));
  const hasRecommendationsWithAvatars = recommendationAvatars.some((avatar) =>
    Boolean(avatar.avatarImage?.uid),
  );

  return (
    <Container>
      {hasActiveSubscriptionAndPublishedPortfolio ? (
        <StyledLink
          onClick={() => {
            track('link', {
              action: 'tapped',
              destination_path: portfolioUrl,
              link_name: 'view_independent_portfolio',
              section: 'profile_sidebar',
            });
          }}
          to={portfolioUrl}
        >
          <StyledText textStyle="subtitleRegular">View my portfolio</StyledText>
          <ChevronRightIcon size={24} />
        </StyledLink>
      ) : null}
      {hasActiveSubscriptionAndPublishedPortfolio && userReviews.count > 0 ? (
        <Divider />
      ) : null}
      {userReviews.count > 0 ? (
        <StyledLink
          onClick={() => {
            track('link', {
              action: 'tapped',
              destination_path: recommendationsUrl,
              link_name: 'view_recommendations',
              section: 'profile_sidebar',
            });
          }}
          to={recommendationsUrl}
        >
          <RecommendedStarContainer>
            <BlackRecommendationStarFullIcon size={16} />
            <StyledText textStyle="subtitleRegular">
              Recommended {userReviews.count}x
            </StyledText>
          </RecommendedStarContainer>
          <RecommendedActionsContainer>
            <MaybeHideAvatars
              hide={!hasRecommendationsWithAvatars}
              isSidenavDocked={isSidenavDocked}
            >
              <AvatarGroup
                data={recommendationAvatars}
                maxCount={2}
                // this empty function ensures the more button gets styled with a pointer cursor.
                onMoreClick={() => {}}
                size={32}
                useAlternativeStackingOrder={false}
              />
            </MaybeHideAvatars>
            <ChevronRightIcon size={24} />
          </RecommendedActionsContainer>
        </StyledLink>
      ) : null}
    </Container>
  );
};
